import { Maybe } from "@/domains/common/types";
import { ZIndex } from "@/domains/design/constants";
import { copyTextToClipboard } from "@/modules/clipboard/copyTextToClipboard";
import { toastModule } from "@/modules/toast";
import { electronModule } from "@/modules/electron";
import { useAppStore } from "@/store";
import { DebugOverlayViewKind } from "@/store/debug/AppStoreDebugStore";
import { CustomSyncErrorKind } from "@/store/sync/operations/errors/SyncError";
import { MDSCheckbox } from "@/mds/components/forms/checkbox";
import { getOperationKindToClassMap } from "@/store/sync/operations/map";
import { SyncOperationKind } from "@/store/sync/types";
import { observer } from "mobx-react-lite";
import { clientEnvModule } from "@/modules/client-env";
import { RecallSdkState } from "@/modules/electron/types";
import { DateTime } from "luxon";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  MDSActionMenuDropdown,
  MDSButton,
  MDSButtonVariant,
  MDSMenuContentItemKind,
  MDSMenuItemSelectionVariant,
  MDSSpacer,
  MDSText,
  MDSButtonSize,
} from "@/mds";
import { SmartViewKind } from "@/domains/smart/constants";
import { resolveSmartContextId } from "@/store/heads-up/SmartContextId";
import styled from "@emotion/styled";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { CacheKey } from "@/modules/cache/constants";
import { useQuery } from "@tanstack/react-query";
import { SortByKind } from "@/modules/lenses/types";
import type { Uuid } from "@/domains/global/identifiers";
import { ImportableNote } from "@/store/one-click-import/types";

const customErrorKinds: Maybe<CustomSyncErrorKind>[] = [
  undefined,
  "INVALID",
  "PERMISSION_DENIED",
  "TRANSIENT",
  "UNKNOWN",
];

// Type for performance memory API
interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: PerformanceMemory;
}

// Performance monitoring sub-component
const PerformanceMetrics = observer(function PerformanceMetrics() {
  const [fps, setFps] = useState(0);
  const [memoryMB, setMemoryMB] = useState(0);
  const [frameTime, setFrameTime] = useState({ max: 0, avg: 0 });
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(performance.now());
  const frameTimes = useRef<number[]>([]);
  const lastUpdateRef = useRef(performance.now());

  useEffect(() => {
    let animationId: number;

    const measurePerformance = (currentTime: number) => {
      // Calculate FPS
      frameCountRef.current++;
      const deltaTime = currentTime - lastTimeRef.current;

      if (deltaTime >= 1000) {
        setFps(Math.round((frameCountRef.current * 1000) / deltaTime));
        frameCountRef.current = 0;
        lastTimeRef.current = currentTime;
      }

      // Continuously track frame times in background
      if (frameTimes.current.length > 0) {
        frameTimes.current.push(currentTime);

        // Keep only last 10 frames for lag detection
        if (frameTimes.current.length > 11) {
          // 11 timestamps = 10 frame deltas
          frameTimes.current.shift();
        }
      } else {
        frameTimes.current.push(currentTime);
      }

      // Update memory usage every 5 seconds
      if (currentTime - lastUpdateRef.current >= 5000) {
        const perfWithMemory = performance as PerformanceWithMemory;
        if (perfWithMemory.memory) {
          setMemoryMB(Math.round(perfWithMemory.memory.usedJSHeapSize / 1024 / 1024));
        }
        lastUpdateRef.current = currentTime;
      }

      animationId = requestAnimationFrame(measurePerformance);
    };

    // Add click listener to analyze recent frame times
    const handleClick = () => {
      if (frameTimes.current.length > 1) {
        // Calculate frame deltas from recent history
        const deltas = [];
        for (let i = 1; i < frameTimes.current.length; i++) {
          deltas.push(frameTimes.current[i] - frameTimes.current[i - 1]);
        }

        if (deltas.length > 0) {
          const maxFrameTime = Math.max(...deltas);
          const avgFrameTime = deltas.reduce((a, b) => a + b, 0) / deltas.length;

          setFrameTime({
            max: Math.round(maxFrameTime),
            avg: Math.round(avgFrameTime),
          });
        }
      }

      // Also update memory immediately on click
      const perfWithMemory = performance as PerformanceWithMemory;
      if (perfWithMemory.memory) {
        setMemoryMB(Math.round(perfWithMemory.memory.usedJSHeapSize / 1024 / 1024));
      }
    };

    document.addEventListener("click", handleClick, true);
    animationId = requestAnimationFrame(measurePerformance);

    return () => {
      cancelAnimationFrame(animationId);
      document.removeEventListener("click", handleClick, true);
    };
  }, []);

  return (
    <StyledPerformanceMetrics>
      <span>FPS: {fps.toString().padStart(2, " ")}</span>
      <span>Frame: {frameTime.max}ms</span>
      {<span>Mem: {memoryMB > 0 ? `${memoryMB}MB` : `...`}</span>}
    </StyledPerformanceMetrics>
  );
});

// Debug content sub-component
const ExpandedDebugContent = observer(function ExpandedDebugContent() {
  const { store } = useAppStore();
  const { buildInfo } = clientEnvModule.buildInfo();

  const operationKinds: Maybe<SyncOperationKind>[] = [
    undefined,
    ...(Object.keys(getOperationKindToClassMap()) as SyncOperationKind[]),
  ];

  const [actionQueueExpanded, setActionQueueExpanded] = useState(true);

  return (
    <StyledDebugContent>
      <StyledCheckboxContainer>
        <MDSCheckbox
          isSelected={store.debug.forceOnboardingMode}
          onChange={() => store.debug.toggleForceOnboardingMode()}
        >
          <MDSText>Force Chat Onboarding Mode</MDSText>
          <MDSText>This will force render special onboarding empty state for chat</MDSText>
        </MDSCheckbox>
      </StyledCheckboxContainer>
      <StyledMockApiErrorRow>
        <MDSSpacer />
        <MDSActionMenuDropdown
          placement="bottom right"
          contentList={customErrorKinds.map(kind => ({
            id: kind ?? "DISABLED",
            kind: MDSMenuContentItemKind.Item,
            label: kind ?? "DISABLED",
            isSelected: store.debug.operationsSubmitApiMockErrorKind === kind,
            selectionVariant: MDSMenuItemSelectionVariant.Checkmark,
            onClick: () => store.debug.setOperationsSubmitApiMockErrorKind(kind),
          }))}
        >
          <MDSButton>{store.debug.operationsSubmitApiMockErrorKind ?? "DISABLED"}</MDSButton>
        </MDSActionMenuDropdown>
        {"|"}
        <MDSActionMenuDropdown
          contentList={operationKinds.map(kind => ({
            id: kind ?? "all",
            kind: MDSMenuContentItemKind.Item,
            label: kind ?? "all",
            isSelected: store.debug.operationsSubmitApiMockErrorOperationKind === kind,
            selectionVariant: MDSMenuItemSelectionVariant.Checkmark,
            onClick: () => store.debug.setOperationsSubmitApiMockErrorOperationKind(kind),
          }))}
        >
          <MDSButton>{store.debug.operationsSubmitApiMockErrorOperationKind ?? "ALL"}</MDSButton>
        </MDSActionMenuDropdown>
      </StyledMockApiErrorRow>
      <StyledDebugInfo onClick={e => e.altKey && setActionQueueExpanded(!actionQueueExpanded)}>
        {!actionQueueExpanded && "<Alt + click to expand action queue info>"}
        {actionQueueExpanded && JSON.stringify(store.sync.actionQueue.friendlyDebugInfo, null, 4)}
      </StyledDebugInfo>
      <br />
      <StyledDebugInfo>
        <span>Commit: {buildInfo.gitCommitSha}</span>
        <br />
        <span>
          Build at:{" "}
          {buildInfo.buildTimestamp.toLocal().toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
        </span>
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});

// Database view content
const ExpandedDatabaseContent = observer(function ExpandedDatabaseContent() {
  const { store } = useAppStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalSizeBytes, setTotalSizeBytes] = useState<number | null>(null);
  const [rowCounts, setRowCounts] = useState<Array<{ table: string; rowCount: number }>>([]);

  const isDbInitialized = store.db.isInitialized();

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
    return `${value} ${sizes[i]}`;
  };

  const refresh = async () => {
    if (!isDbInitialized) return;
    setLoading(true);
    setError(null);
    try {
      const sizeBytes = await store.db.stats.getTotalSizeBytes();
      setTotalSizeBytes(sizeBytes);

      const counts = await store.db.stats.getRowCountsByTable();
      setRowCounts(counts);
    } catch (unknownErr) {
      const err = unknownErr as Error;
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDbInitialized]);

  if (!isDbInitialized) {
    return <StyledDebugContent>Heads-up DB not initialized.</StyledDebugContent>;
  }

  return (
    <StyledDebugContent>
      <StyledButtonGroup>
        <MDSButton variant={MDSButtonVariant.Transparent} onClick={refresh} isLoading={loading}>
          Refresh
        </MDSButton>
      </StyledButtonGroup>

      {error && <StyledErrorText>Error: {error}</StyledErrorText>}

      <StyledDebugInfo>
        <StyledStorageInfo>
          <strong>Total storage:</strong>{" "}
          {totalSizeBytes !== null ? formatBytes(totalSizeBytes) : "..."}
        </StyledStorageInfo>
        <div>
          <strong>Row counts (public):</strong>
        </div>
        <div>
          {rowCounts.length === 0 && "..."}
          {rowCounts.length > 0 && (
            <StyledUnorderedList>
              {rowCounts.map(({ table, rowCount }) => (
                <StyledListItem key={table}>
                  <span>{table}</span>
                  <span>{rowCount.toLocaleString()}</span>
                </StyledListItem>
              ))}
            </StyledUnorderedList>
          )}
        </div>
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});

/**
 * Debug content component for Heads Up sidebar state inspection
 */
const ExpandedHeadsUpContent = observer(function ExpandedHeadsUpContent() {
  const { store } = useAppStore();
  const spaceAccountId = store.spaceAccounts.myPersonalSpaceAccountId ?? null;
  const liveNoteId = store.routing.noteIdParam ?? null;

  const contextId = resolveSmartContextId({
    spaceAccountId,
    activeNoteId: liveNoteId ?? null,
    smartViewKind: SmartViewKind.HeadsUp,
  });

  const { context } = store.db.smartContexts.useById({
    contextId,
  });

  const smartContextId = context?.id ?? contextId;

  const { view } = store.db.smartViews.useLatestByContextId({
    smartContextId,
  });

  const { cards } = store.db.smartCards.useCardsBySmartViewId({
    smartViewId: view?.id ?? null,
  });

  const summary = {
    noteId: liveNoteId,
    contextId: smartContextId,
    contextPayload: context?.payload_json ?? null,
    viewId: view?.id ?? null,
    viewRefreshedAt: view?.refreshed_at ?? null,
    cardCount: cards.length,
  };

  return (
    <StyledDebugContent>
      <StyledDebugInfo>{JSON.stringify(summary, null, 2)}</StyledDebugInfo>
      <StyledDebugInfo>{JSON.stringify(cards, null, 2)}</StyledDebugInfo>
    </StyledDebugContent>
  );
});

/**
 * Shows the number of observables in memory at any given time
 */
const ExpandedMemoryContent = observer(function ExpandedMemoryContent() {
  const { store } = useAppStore();
  return (
    <StyledDebugContent>
      <StyledDebugInfo>
        <StyledSectionTitle># Observables in Memory (Current / Loaded / Freed)</StyledSectionTitle>
        {Object.entries(store.sync.stores)
          .filter(([, storeInstance]) => storeInstance !== undefined)
          .map(([kind, storeInstance]) => {
            let freedCount = 0;
            let currentCount = 0;
            for (const weakRefOrUndefined of storeInstance!.pool.values()) {
              if (weakRefOrUndefined) {
                const model = weakRefOrUndefined.deref();
                if (model === undefined) {
                  freedCount++;
                } else {
                  currentCount++;
                }
              }
            }
            return (
              <StyledMemoryRow key={kind}>
                <span>{kind.replace(/_/g, " ")}</span>
                <span>
                  C: {currentCount} | L: {storeInstance!.pool.size} | F: {freedCount}
                </span>
              </StyledMemoryRow>
            );
          })}
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});

/**
 * Shows the number of meeting detection debug events
 */
const ExpandedElectronContent = observer(function ExpandedElectronContent() {
  const { store } = useAppStore();
  const [isTestingShutdown, setIsTestingShutdown] = useState(false);
  const isRecallReady = store.electron.recallSdkState === RecallSdkState.Running;

  const handleTestUnexpectedShutdown = useCallback(async () => {
    if (isTestingShutdown || !clientEnvModule.isDesktop() || !isRecallReady) return;

    setIsTestingShutdown(true);
    try {
      await electronModule.recall.testUnexpectedShutdown?.();
      toastModule.triggerToast({ content: "Triggered Recall unexpected shutdown test." });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "Error testing unexpected shutdown",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
      toastModule.triggerToast({ content: "Recall shutdown test failed. Check logs." });
    } finally {
      setIsTestingShutdown(false);
    }
  }, [isTestingShutdown, isRecallReady]);

  return (
    <StyledDebugContent>
      <StyledSectionTitle>Electron</StyledSectionTitle>
      <StyledDebugInfo>
        <div>
          recallSdkState: <b>{store.electron.recallSdkState}</b>
        </div>
        {store.electron.recallSdkStateErrorMessage ? (
          <div>error: {store.electron.recallSdkStateErrorMessage}</div>
        ) : null}
        {clientEnvModule.isDesktop() && (
          <MDSButton
            variant={MDSButtonVariant.Default}
            size={MDSButtonSize.Small}
            onClick={handleTestUnexpectedShutdown}
            isLoading={isTestingShutdown}
          >
            Test unexpected shutdown
          </MDSButton>
        )}
      </StyledDebugInfo>
      <StyledSectionTitle># Meeting Detection Debug Events</StyledSectionTitle>
      <StyledSectionTitle>Electron info</StyledSectionTitle>
      <StyledDebugInfo>{JSON.stringify(store.electron.electronAppInfo, null, 2)}</StyledDebugInfo>

      <StyledSectionTitle>Meeting Detection Debug Events</StyledSectionTitle>
      <StyledDebugInfo>
        {store.electron.meetingDetectionDebugEvents.map(event => {
          const { timestamp, data, kind } = event;
          return (
            <Fragment key={event.timestamp.toJSDate().getTime()}>
              <b>
                {timestamp.toFormat("HH:mm:ss.SSS")} {kind}
              </b>
              {Object.entries(data).map(([key, value]) =>
                typeof value === "object" ? null : (
                  <>
                    <br />
                    {key}: {JSON.stringify(value, null, 2)}
                  </>
                )
              )}
              <br />
            </Fragment>
          );
        })}
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});

const ExpandedCacheContent = observer(function ExpandedCacheContent() {
  const { store } = useAppStore();

  const perDeviceOnboardingInfoResult = useQuery({
    queryKey: [CacheKey.PerDeviceOnboardingInfo],
    queryFn: () => store.accountCache.get(CacheKey.PerDeviceOnboardingInfo),
  });

  return (
    <StyledDebugContent>
      <StyledSectionTitle>PerDeviceOnboardingInfo</StyledSectionTitle>
      <div>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => perDeviceOnboardingInfoResult.refetch()}
        >
          Refresh
        </MDSButton>
      </div>
      <StyledDebugInfo>
        {JSON.stringify(perDeviceOnboardingInfoResult.data, null, 2)}
        <br />
        <br />
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => {
            store.accountCache.del(CacheKey.PerDeviceOnboardingInfo);
            perDeviceOnboardingInfoResult.refetch();
          }}
        >
          Clear
        </MDSButton>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => {
            store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, {
              hasDismissedMeetingOnboardingSystemPermissions: false,
              hasDismissedVoiceOnboardingSystemPermissions: false,
            });
            perDeviceOnboardingInfoResult.refetch();
          }}
        >
          Set isDismissed to False
        </MDSButton>
        <MDSButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.Default}
          onClick={() => {
            store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, {
              hasDismissedMeetingOnboardingSystemPermissions: true,
              hasDismissedVoiceOnboardingSystemPermissions: true,
            });
            perDeviceOnboardingInfoResult.refetch();
          }}
        >
          Set isDismissed to True
        </MDSButton>
        <br />
        You&apos;ll need to quit-and-reopen for Mem to pick up MacOS permission changes.
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});

const ExpandedOneClickImportContent = observer(function ExpandedOneClickImportContent() {
  const { store } = useAppStore();

  const loadMockData = () => {
    // Limited to 5 unique tags and 5 unique folders for easier testing
    const mockNotes: ImportableNote[] = [
      {
        id: "mock-1" as Uuid,
        title: "Project Planning Meeting Notes",
        markdown: "# Project Planning\n\nDiscussed Q1 roadmap and resource allocation.",
        folders: ["work"],
        tags: ["important", "work"],
        isSelected: true,
        isLocked: false,
        isPinned: true,
        createdAt: "2024-01-15T10:00:00Z",
        updatedAt: "2024-01-20T14:30:00Z",
        subtitle: "Discussed Q1 roadmap and resource allocation.",
      },
      {
        id: "mock-2" as Uuid,
        title: "API Design Ideas",
        markdown: "# API Design\n\nConsider REST vs GraphQL for new endpoints.",
        folders: ["Projects"],
        tags: ["ideas", "work"],
        isSelected: true,
        isLocked: false,
        isPinned: false,
        createdAt: "2024-01-18T09:15:00Z",
        updatedAt: "2024-01-22T11:45:00Z",
        subtitle: "Consider REST vs GraphQL for new endpoints.",
      },
      {
        id: "mock-3" as Uuid,
        title: "Team Standup Notes",
        markdown: "# Daily Standup\n\n- Alice: Working on authentication\n- Bob: Fixing bugs",
        folders: ["work"],
        tags: ["work"],
        isSelected: true,
        isLocked: false,
        isPinned: false,
        createdAt: "2024-02-01T09:00:00Z",
        updatedAt: "2024-02-01T09:30:00Z",
        subtitle: "Alice: Working on authentication",
      },
      {
        id: "mock-4" as Uuid,
        title: "Recipe: Chocolate Chip Cookies",
        markdown: "# Chocolate Chip Cookies\n\nIngredients:\n- 2 cups flour\n- 1 cup butter",
        folders: ["Personal"],
        tags: ["personal"],
        isSelected: true,
        isLocked: false,
        isPinned: true,
        createdAt: "2024-01-10T16:00:00Z",
        updatedAt: "2024-01-25T18:20:00Z",
        subtitle: "Ingredients: 2 cups flour, 1 cup butter",
      },
      {
        id: "mock-5" as Uuid,
        title: "Book Recommendations",
        markdown:
          "# Books to Read\n\n- The Pragmatic Programmer\n- Clean Code\n- Designing Data-Intensive Applications",
        folders: ["Personal"],
        tags: ["learning", "personal"],
        isSelected: true,
        isLocked: false,
        isPinned: false,
        createdAt: "2024-01-05T20:00:00Z",
        updatedAt: "2024-02-10T21:15:00Z",
        subtitle: "The Pragmatic Programmer",
      },
      {
        id: "mock-6" as Uuid,
        title: "Sensitive Information",
        markdown: "This note is locked.",
        folders: ["Personal"],
        tags: ["important", "personal"],
        isSelected: false,
        isLocked: true,
        lockedReason: "This note is password-protected in Apple Notes",
        isPinned: false,
        createdAt: "2024-01-12T14:00:00Z",
        updatedAt: "2024-01-12T14:05:00Z",
        subtitle: "This note is locked.",
      },
      {
        id: "mock-7" as Uuid,
        title: "Quick Thoughts",
        markdown: "# Random Ideas\n\nMaybe we should try a different approach to caching.",
        folders: [],
        tags: ["ideas"],
        isSelected: true,
        isLocked: false,
        isPinned: false,
        createdAt: "2024-02-05T11:30:00Z",
        updatedAt: "2024-02-05T11:45:00Z",
        subtitle: "Maybe we should try a different approach to caching.",
      },
      {
        id: "mock-8" as Uuid,
        title: "Shopping List",
        markdown: "# Groceries\n\n- Milk\n- Eggs\n- Bread\n- Cheese",
        folders: [],
        tags: ["personal"],
        isSelected: true,
        isLocked: false,
        isPinned: true,
        createdAt: "2024-02-08T08:00:00Z",
        updatedAt: "2024-02-08T10:00:00Z",
        subtitle: "Milk, Eggs, Bread, Cheese",
      },
      {
        id: "mock-9" as Uuid,
        title: "React Hooks Deep Dive",
        markdown: "# React Hooks\n\nuseEffect, useState, useContext - understanding the lifecycle.",
        folders: ["Learning"],
        tags: ["learning", "important"],
        isSelected: true,
        isLocked: false,
        isPinned: false,
        createdAt: "2024-01-20T13:00:00Z",
        updatedAt: "2024-02-03T15:30:00Z",
        subtitle: "useEffect, useState, useContext - understanding the lifecycle.",
      },
      {
        id: "mock-10" as Uuid,
        title: "TypeScript Best Practices",
        markdown:
          "# TypeScript Tips\n\n- Use strict mode\n- Avoid any type\n- Leverage type inference",
        folders: ["Learning"],
        tags: ["learning"],
        isSelected: true,
        isLocked: false,
        isPinned: false,
        createdAt: "2024-01-28T10:00:00Z",
        updatedAt: "2024-02-07T12:00:00Z",
        subtitle: "Use strict mode, Avoid any type, Leverage type inference",
      },
      {
        id: "mock-11" as Uuid,
        title: "Trip to Japan - Itinerary",
        markdown: "# Japan Trip\n\nDay 1: Tokyo\nDay 2: Kyoto\nDay 3: Osaka",
        folders: ["Notes"],
        tags: ["important", "personal"],
        isSelected: true,
        isLocked: false,
        isPinned: true,
        createdAt: "2024-01-03T19:00:00Z",
        updatedAt: "2024-01-30T20:00:00Z",
        subtitle: "Day 1: Tokyo, Day 2: Kyoto, Day 3: Osaka",
      },
      {
        id: "mock-12" as Uuid,
        title: "Workout Routine",
        markdown:
          "# Weekly Workout\n\nMonday: Chest & Triceps\nWednesday: Back & Biceps\nFriday: Legs",
        folders: ["Notes"],
        tags: ["personal"],
        isSelected: true,
        isLocked: false,
        isPinned: false,
        createdAt: "2024-02-01T07:00:00Z",
        updatedAt: "2024-02-09T07:30:00Z",
        subtitle: "Monday: Chest & Triceps, Wednesday: Back & Biceps",
      },
      {
        id: "mock-13" as Uuid,
        title: "Conference Talk Ideas",
        markdown:
          "# Talk Proposals\n\n- Building Scalable APIs\n- Frontend Performance Optimization",
        folders: ["Projects"],
        tags: ["ideas", "important", "work"],
        isSelected: true,
        isLocked: false,
        isPinned: false,
        createdAt: "2024-01-25T16:00:00Z",
        updatedAt: "2024-02-04T17:00:00Z",
        subtitle: "Building Scalable APIs, Frontend Performance Optimization",
      },
      {
        id: "mock-14" as Uuid,
        title: "Gift Ideas for Sarah",
        markdown: "# Birthday Gift Ideas\n\n- Book about design\n- Art supplies\n- Concert tickets",
        folders: ["Notes"],
        tags: ["ideas", "personal"],
        isSelected: true,
        isLocked: false,
        isPinned: false,
        createdAt: "2024-02-02T15:00:00Z",
        updatedAt: "2024-02-06T16:00:00Z",
        subtitle: "Book about design, Art supplies, Concert tickets",
      },
      {
        id: "mock-15" as Uuid,
        title: "Bug Fix Notes",
        markdown:
          "# Bug Investigation\n\nIssue: Memory leak in useEffect\nSolution: Add proper cleanup function",
        folders: ["Projects"],
        tags: ["work", "important"],
        isSelected: true,
        isLocked: false,
        isPinned: false,
        createdAt: "2024-02-07T14:00:00Z",
        updatedAt: "2024-02-10T10:30:00Z",
        subtitle: "Issue: Memory leak in useEffect",
      },
    ];

    store.oneClickImport.clear();
    store.oneClickImport.addNotes(mockNotes);
    store.oneClickImport.setSortOption(SortByKind.LastModified);
    store.oneClickImport.openModal();

    toastModule.triggerToast({
      content: `Loaded ${mockNotes.length} mock notes`,
    });
  };

  return (
    <StyledDebugContent>
      <StyledDebugInfo>
        <MDSText>One-Click Import</MDSText>
        <MDSText>
          This feature allows you to import multiple notes at once from external sources like Apple
          Notes.
        </MDSText>

        <StyledButtonGroup>
          <MDSButton name="Load Fake Data" onClick={loadMockData}>
            Load Fake Data
          </MDSButton>

          <MDSButton
            name="Import Apple Notes"
            onClick={async () => {
              store.oneClickImport.startImport();
            }}
          >
            Import from Apple Notes (via IPC)
          </MDSButton>
        </StyledButtonGroup>
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});

/**
 * This is a debugging overlay which displays various pieces of info about local application state.
 * Most notably, it displays the current sync action queue, and allows for copying the queue's debug info to the clipboard.
 */
export const DebugInfoOverlay = observer(function DebugInfoOverlay() {
  const { store } = useAppStore();

  if (!store.debug.debugModeEnabled) {
    return null;
  }

  const isOpen = store.debug.debugModeToolbarExpanded;
  const toggleOpen = () => store.debug.toggleDebugModeToolbar();

  return (
    <>
      {/* Overlay content when expanded */}
      {isOpen && (
        <StyledOverlayContent>
          {store.debug.debugOverlayView === DebugOverlayViewKind.Standard && (
            <ExpandedDebugContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Database && (
            <ExpandedDatabaseContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Account && (
            <ExpandedFeatureFlagsContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.FeatureFlags && (
            <ExpandedFeatureFlagsManagementContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.HeadsUp && (
            <ExpandedHeadsUpContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Memory && (
            <ExpandedMemoryContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Electron && (
            <ExpandedElectronContent />
          )}
          {store.debug.debugOverlayView === DebugOverlayViewKind.Cache && <ExpandedCacheContent />}
          {store.debug.debugOverlayView === DebugOverlayViewKind.OneClickImport && (
            <ExpandedOneClickImportContent />
          )}
        </StyledOverlayContent>
      )}

      {/* Navigation bar - visible when expanded */}
      {isOpen && (
        <StyledNavBar>
          <StyledNavRow>
            <StyledNavLeft>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Standard)}
              >
                Standard
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Database)}
              >
                DB
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.HeadsUp)}
              >
                Heads Up
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Account)}
              >
                Account
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.FeatureFlags)}
              >
                Flags
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Memory)}
              >
                Memory
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Electron)}
              >
                Electron
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Small}
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.debug.setDebugOverlayView(DebugOverlayViewKind.Cache)}
              >
                Cache
              </MDSButton>
            </StyledNavLeft>
          </StyledNavRow>
          <StyledNavRow>
            <StyledNavRight>
              {clientEnvModule.isDesktop() && (
                <MDSButton
                  size={MDSButtonSize.Small}
                  variant={MDSButtonVariant.Transparent}
                  onClick={() =>
                    store.debug.setDebugOverlayView(DebugOverlayViewKind.OneClickImport)
                  }
                >
                  One-Click Import
                </MDSButton>
              )}
              <MDSButton
                variant={MDSButtonVariant.Transparent}
                onClick={() => store.sync.actionQueue.saveDebugInfoToFile()}
              >
                Save to file
              </MDSButton>
              <MDSButton
                variant={MDSButtonVariant.Transparent}
                onClick={async () => {
                  const fullDebugInfo = store.sync.actionQueue.fullDebugInfo;
                  await copyTextToClipboard({
                    text: JSON.stringify(fullDebugInfo, null, 4),
                  });
                  toastModule.triggerToast({ content: "Copied queue debug info to clipboard." });
                }}
              >
                Copy Info
              </MDSButton>
            </StyledNavRight>
          </StyledNavRow>
        </StyledNavBar>
      )}

      {/* Bottom bar - always visible */}
      <StyledBottomBar isOpen={isOpen}>
        <StyledHeader>
          {!isOpen && (
            <>
              <PerformanceMetrics />
              <MinimizeButton onClick={toggleOpen} variant={MDSButtonVariant.Transparent}>
                Expand
              </MinimizeButton>
            </>
          )}
          {isOpen && (
            <>
              <PerformanceMetrics />
              <StyledHeaderButtonGroup>
                <MinimizeButton onClick={toggleOpen} variant={MDSButtonVariant.Transparent}>
                  Minimize
                </MinimizeButton>
              </StyledHeaderButtonGroup>
            </>
          )}
        </StyledHeader>
      </StyledBottomBar>
    </>
  );
});

const MinimizeButton = styled(MDSButton)`
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  padding: 2px 4px;
`;

const StyledBottomBar = styled.div<{ isOpen: boolean }>`
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 4px;
  bottom: 0;
  padding: 4px 8px;
  position: fixed;
  right: 0;
  font-size: 14px;
  width: ${({ isOpen }) => (isOpen ? "600px" : "auto")};
  height: 34px;
  z-index: ${ZIndex.DebugOverlay};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0.8)};
  transition: all 0.2s ease;
`;

const StyledOverlayContent = styled.div`
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 4px;
  bottom: 120px; /* Position above nav bar (80px for two rows) and bottom bar (34px) */
  padding: 16px;
  position: fixed;
  right: 0;
  font-size: 14px;
  width: 600px;
  max-height: calc(100vh - 140px);
  overflow: auto;
  z-index: ${ZIndex.DebugOverlay - 1};
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
`;

const StyledNavBar = styled.div`
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 4px;
  bottom: 42px; /* Just above the bottom bar */
  padding: 4px 8px;
  position: fixed;
  right: 0;
  font-size: 14px;
  width: 600px;
  z-index: ${ZIndex.DebugOverlay};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StyledNavRow = styled.div`
  display: flex;
  align-items: center;
`;

const StyledNavLeft = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const StyledNavRight = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  gap: 16px;
`;

const StyledHeaderButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledPerformanceMetrics = styled.div`
  display: flex;
  gap: 12px;
  font-size: 12px;
  font-family: monospace;
  color: ${({ theme }) => theme.colors.text.primary};
  align-items: center;

  & > span {
    white-space: nowrap;

    &:nth-of-type(1) {
      width: 60px; /* FPS: XX */
    }

    &:nth-of-type(2) {
      width: 80px; /* Frame: XXms */
    }

    &:nth-of-type(3) {
      width: 80px; /* Mem: XXXmb */
    }
  }
`;

const StyledDebugContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledMockApiErrorRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const StyledDebugInfo = styled.pre`
  font-size: 12px;
  overflow: auto;
  max-height: 300px;
  background-color: ${({ theme }) => theme.colors.surface.modal};
  padding: 12px;
  border-radius: 4px;
  font-family: monospace;
`;

const StyledErrorText = styled.div`
  color: ${({ theme }) => theme.colors.text.danger};
`;

const StyledFeatureFlagItem = styled.li<{ isOn: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  margin-bottom: 4px;
  background-color: ${({ theme, isOn }) =>
    isOn ? theme.colors.background.banner.emphasis : theme.colors.background.banner.default};
`;

const StyledFeatureFlagStatus = styled.span<{ isOn: boolean }>`
  font-weight: 600;
  color: ${({ theme, isOn }) => (isOn ? theme.colors.text.primary : theme.colors.text.secondary)};
  text-transform: uppercase;
  font-size: 11px;
`;

const StyledCheckboxContainer = styled.div`
  margin-bottom: 16px;
  padding: 8px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.background.banner.default};
`;

const StyledButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

const StyledStorageInfo = styled.div`
  margin-bottom: 8px;
`;

const StyledUnorderedList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const StyledListItem = styled.li`
  display: flex;
  justify-content: space-between;
`;

const StyledMemoryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

  &:last-child {
    border-bottom: none;
  }
`;

const StyledSectionTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
`;

// Feature flags management view content (read-only)
const ExpandedFeatureFlagsManagementContent = observer(
  function ExpandedFeatureFlagsManagementContent() {
    const { store } = useAppStore();
    const flags = store.spaceAccountFeatureFlags.featureFlags;
    const entries = Object.entries(flags).sort(([a], [b]) => a.localeCompare(b));

    return (
      <StyledDebugContent>
        <StyledDebugInfo>
          {entries.length === 0 ? (
            <div>No feature flags loaded.</div>
          ) : (
            <StyledUnorderedList>
              {entries.map(([flagKey, value]) => {
                const isOn = Boolean(value);

                return (
                  <StyledFeatureFlagItem key={flagKey} isOn={isOn}>
                    <span>{flagKey}</span>
                    <StyledFeatureFlagStatus isOn={isOn}>
                      {isOn ? "ON" : "OFF"}
                    </StyledFeatureFlagStatus>
                  </StyledFeatureFlagItem>
                );
              })}
            </StyledUnorderedList>
          )}
        </StyledDebugInfo>
      </StyledDebugContent>
    );
  }
);

// Feature flags view content (Account tab - read-only)
const ExpandedFeatureFlagsContent = observer(function ExpandedFeatureFlagsContent() {
  const { store } = useAppStore();
  const flags = store.spaceAccountFeatureFlags.featureFlags;
  const entries = Object.entries(flags).sort(([a], [b]) => a.localeCompare(b));

  return (
    <StyledDebugContent>
      <StyledDebugInfo>
        {entries.length === 0 ? (
          <div>No feature flags loaded.</div>
        ) : (
          <StyledUnorderedList>
            {entries.map(([key, value]) => {
              const isOn = Boolean(value);
              return (
                <StyledFeatureFlagItem key={key} isOn={isOn}>
                  <span>{key}</span>
                  <StyledFeatureFlagStatus isOn={isOn}>
                    {isOn ? "ON" : "OFF"}
                  </StyledFeatureFlagStatus>
                </StyledFeatureFlagItem>
              );
            })}
          </StyledUnorderedList>
        )}
      </StyledDebugInfo>
    </StyledDebugContent>
  );
});
